import type { SearchResult, DocSection, DocItem, Language } from './types.js';
import { docSections, docContent } from './content.js';
import { tagManager } from './tags.js';
import { loadDocContent } from './loader.js';

export class DocumentationSearch {
	private searchIndex: Map<string, SearchResult[]> = new Map();

	private initPromise: Promise<void>;
	
	constructor() {
		this.initPromise = this.buildSearchIndex();
	}
	
	private async ensureInitialized(): Promise<void> {
		await this.initPromise;
	}

	private async buildSearchIndex(): Promise<void> {
		// Build search index for all languages
		for (const [lang, sections] of Object.entries(docSections)) {
			const results: SearchResult[] = [];
			
			for (const section of sections) {
				for (const item of section.items) {
					// Try to load from dynamic content first
					let dynamicContent = null;
					try {
						dynamicContent = await loadDocContent(item.slug, lang as Language);
					} catch (error) {
						// Ignore load errors, fallback to static content
					}
					
					if (dynamicContent) {
						results.push({
							id: item.id,
							title: item.title,
							description: item.description || dynamicContent.description || '',
							slug: item.slug,
							section: section.title,
							tags: dynamicContent.tags || [],
							relevance: 0
						});
					} else {
						// Fallback to static content if exists
						const content = docContent[item.id];
						results.push({
							id: item.id,
							title: item.title,
							description: item.description || '',
							slug: item.slug,
							section: section.title,
							tags: content?.tags || [],
							relevance: 0
						});
					}
					
					// Also handle children items
					if (item.children) {
						for (const child of item.children) {
							let childContent = null;
							try {
								childContent = await loadDocContent(child.slug, lang as Language);
							} catch (error) {
								// Ignore load errors, fallback to static content
							}
							
							if (childContent) {
								results.push({
									id: child.id,
									title: child.title,
									description: child.description || childContent.description || '',
									slug: child.slug,
									section: section.title,
									tags: childContent.tags || [],
									relevance: 0
								});
							} else {
								// Fallback for children
								const childStaticContent = docContent[child.id];
								results.push({
									id: child.id,
									title: child.title,
									description: child.description || '',
									slug: child.slug,
									section: section.title,
									tags: childStaticContent?.tags || [],
									relevance: 0
								});
							}
						}
					}
				}
			}
			
			this.searchIndex.set(lang, results);
		}
	}

	async search(query: string, language: Language = 'en'): Promise<SearchResult[]> {
		await this.ensureInitialized();
		
		if (!query.trim()) return [];
		
		// Check if this is a tag search (starts with #)
		if (query.startsWith('#')) {
			return this.searchByTag(query.slice(1), language);
		}
		
		// Check if query contains hashtags mixed with regular search
		const hashtagRegex = /#(\w+)/g;
		const hashtags: string[] = [];
		let cleanQuery = query;
		
		let match;
		while ((match = hashtagRegex.exec(query)) !== null) {
			hashtags.push(match[1].toLowerCase());
			cleanQuery = cleanQuery.replace(match[0], '').trim();
		}
		
		// If we have hashtags, combine tag search with regular search
		if (hashtags.length > 0) {
			return this.searchWithTags(cleanQuery, hashtags, language);
		}
		
		// Regular search
		const results = this.searchIndex.get(language) || [];
		const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
		
		return results
			.map(item => {
				let relevance = 0;
				const title = item.title.toLowerCase();
				const description = item.description.toLowerCase();
				
				// Score based on exact matches and partial matches
				searchTerms.forEach(term => {
					// Title exact match (highest score)
					if (title === term) relevance += 10;
					// Title contains term (high score)
					else if (title.includes(term)) relevance += 5;
					// Description contains term (medium score)
					else if (description.includes(term)) relevance += 3;
					// Section contains term (low score)
					else if (item.section.toLowerCase().includes(term)) relevance += 1;
					// Tag match (medium-high score)
					else if (item.tags?.some(tag => tag.toLowerCase().includes(term))) relevance += 4;
				});
				
				// Boost relevance for multiple term matches
				const matchingTerms = searchTerms.filter(term => 
					title.includes(term) || description.includes(term) || 
					item.tags?.some(tag => tag.toLowerCase().includes(term))
				);
				relevance *= Math.max(1, matchingTerms.length * 0.5);
				
				return {
					...item,
					relevance,
					highlighted: this.highlightMatches(item, searchTerms)
				};
			})
			.filter(item => item.relevance > 0)
			.sort((a, b) => b.relevance - a.relevance)
			.slice(0, 10); // Limit to top 10 results
	}
	
	private async searchByTag(tagName: string, language: Language = 'en'): Promise<SearchResult[]> {
		await this.ensureInitialized();
		
		const tagInfo = await tagManager.getTagInfo(tagName, language);
		if (!tagInfo) return [];
		
		const results = this.searchIndex.get(language) || [];
		return results
			.filter(item => {
				// Check if this item has the tag
				return item.tags?.some(tag => tag.toLowerCase() === tagName.toLowerCase());
			})
			.map(item => ({
				...item,
				relevance: 10, // High relevance for exact tag match
				highlighted: {
					title: item.title,
					description: item.description
				}
			}))
			.slice(0, 20); // Allow more results for tag searches
	}
	
	private async searchWithTags(query: string, tags: string[], language: Language = 'en'): Promise<SearchResult[]> {
		await this.ensureInitialized();
		
		const results = this.searchIndex.get(language) || [];
		const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
		
		return results
			.map(item => {
				let relevance = 0;
				const title = item.title.toLowerCase();
				const description = item.description.toLowerCase();
				
				// Must have all specified tags
				const hasAllTags = tags.every(tag => 
					item.tags?.some(itemTag => itemTag.toLowerCase() === tag)
				);
				
				if (!hasAllTags && tags.length > 0) {
					return { ...item, relevance: 0 };
				}
				
				// Tag match bonus
				if (hasAllTags) relevance += tags.length * 5;
				
				// Regular search scoring if there's a query
				if (searchTerms.length > 0) {
					searchTerms.forEach(term => {
						if (title === term) relevance += 10;
						else if (title.includes(term)) relevance += 5;
						else if (description.includes(term)) relevance += 3;
						else if (item.section.toLowerCase().includes(term)) relevance += 1;
					});
				}
				
				return {
					...item,
					relevance,
					highlighted: this.highlightMatches(item, [...searchTerms, ...tags.map(t => `#${t}`)])
				};
			})
			.filter(item => item.relevance > 0)
			.sort((a, b) => b.relevance - a.relevance)
			.slice(0, 15);
	}

	private highlightMatches(item: SearchResult, searchTerms: string[]): SearchResult['highlighted'] {
		const highlight = (text: string) => {
			let highlighted = text;
			searchTerms.forEach(term => {
				const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
				highlighted = highlighted.replace(regex, '<mark>$1</mark>');
			});
			return highlighted;
		};

		return {
			title: highlight(item.title),
			description: highlight(item.description)
		};
	}

	private escapeRegex(string: string): string {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}
}

// Singleton instance
export const documentationSearch = new DocumentationSearch();